/**
 * Input Component
 *
 * A form input component with label, error states, and icons.
 * Includes comprehensive logging for form interactions.
 */

import { forwardRef, type InputHTMLAttributes, type ReactNode, useState, useId } from 'react';
import { cn } from '@/utils';
import { Logger, LogCategory } from '@/utils/logger';
import { AlertCircle, Eye, EyeOff } from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  hint?: string;
  helperText?: string; // Alias for hint
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
}

// ============================================================================
// Styles
// ============================================================================

const baseStyles = `
  w-full rounded-lg border bg-white
  transition-all duration-200
  placeholder:text-slate-400
  focus:outline-none focus:ring-2 focus:ring-offset-0
  disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed
`;

const sizeStyles = {
  sm: 'h-8 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-4 text-base',
};

const stateStyles = {
  default: `
    border-slate-300
    hover:border-slate-400
    focus:border-primary-500 focus:ring-primary-500/20
  `,
  error: `
    border-danger-500
    focus:border-danger-500 focus:ring-danger-500/20
  `,
};

// ============================================================================
// Component
// ============================================================================

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      error,
      hint,
      helperText,
      leftIcon,
      rightIcon,
      size = 'md',
      fullWidth = true,
      type = 'text',
      id,
      onFocus,
      onBlur,
      onChange,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const [showPassword, setShowPassword] = useState(false);

    const isPassword = type === 'password';
    const inputType = isPassword && showPassword ? 'text' : type;

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      Logger.debug(LogCategory.UI, 'Input focused', 'Input', {
        inputId,
        name: props.name,
      });
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      Logger.debug(LogCategory.UI, 'Input blurred', 'Input', {
        inputId,
        name: props.name,
        hasValue: !!e.target.value,
      });
      onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      Logger.debug(LogCategory.UI, 'Input changed', 'Input', {
        inputId,
        name: props.name,
        valueLength: e.target.value.length,
      });
      onChange?.(e);
    };

    const togglePasswordVisibility = () => {
      Logger.debug(LogCategory.UI, 'Password visibility toggled', 'Input', {
        inputId,
        nowVisible: !showPassword,
      });
      setShowPassword(!showPassword);
    };

    return (
      <div className={cn('flex flex-col gap-1.5', fullWidth && 'w-full')}>
        {label && (
          <label
            htmlFor={inputId}
            className="text-sm font-medium text-slate-700"
          >
            {label}
            {props.required && <span className="text-danger-500 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              {leftIcon}
            </div>
          )}

          <input
            ref={ref}
            id={inputId}
            type={inputType}
            className={cn(
              baseStyles,
              sizeStyles[size],
              error ? stateStyles.error : stateStyles.default,
              leftIcon && 'pl-10',
              (rightIcon || isPassword) && 'pr-10',
              className
            )}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            aria-invalid={!!error}
            aria-describedby={error ? `${inputId}-error` : (hint || helperText) ? `${inputId}-hint` : undefined}
            {...props}
          />

          {isPassword && (
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </button>
          )}

          {rightIcon && !isPassword && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
              {rightIcon}
            </div>
          )}
        </div>

        {error && (
          <div
            id={`${inputId}-error`}
            className="flex items-center gap-1.5 text-sm text-danger-600"
            role="alert"
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {(hint || helperText) && !error && (
          <p
            id={`${inputId}-hint`}
            className="text-sm text-slate-500"
          >
            {hint || helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

// ============================================================================
// Search Input Variant
// ============================================================================

import { Search, X } from 'lucide-react';

export interface SearchInputProps extends Omit<InputProps, 'leftIcon' | 'rightIcon' | 'type'> {
  onClear?: () => void;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({ value, onClear, ...props }, ref) => {
    const hasValue = !!value;

    return (
      <Input
        ref={ref}
        type="search"
        leftIcon={<Search className="h-4 w-4" />}
        rightIcon={
          hasValue && onClear ? (
            <button
              type="button"
              onClick={() => {
                Logger.debug(LogCategory.UI, 'Search cleared', 'SearchInput');
                onClear();
              }}
              className="hover:text-slate-600 focus:outline-none"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          ) : undefined
        }
        value={value}
        {...props}
      />
    );
  }
);

SearchInput.displayName = 'SearchInput';
